import React from 'react';
import { Button, Checkbox, Text } from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronRight20Regular } from '@fluentui/react-icons';
import type { PolicyRule } from '../types';
import { RULE_ROW_STYLES, TAG_STYLES, GRID_STYLES, SECTION_HEADER_STYLES, COLORS, RULE_ROW_PADDING } from '../constants';

interface RuleRowProps {
  rule: PolicyRule;
  isExpanded: boolean;
  onToggle: () => void;
  showCheckbox?: boolean;
  isChecked?: boolean;
  onCheckChange?: (checked: boolean) => void;
  isDisabled?: boolean;
  disabledReason?: string;
  comparisonStatus?: 'deployed' | 'missing' | 'none';
  variant?: 'default' | 'readonly';
  showDeploymentTag?: boolean;
  isDeployed?: boolean;
}

/**
 * Reusable expandable rule row component
 * Supports checkbox, disabled state, and comparison status coloring
 */
export const RuleRow: React.FC<RuleRowProps> = ({ 
  rule, 
  isExpanded, 
  onToggle,
  showCheckbox = false,
  isChecked = false,
  onCheckChange,
  isDisabled = false,
  disabledReason = '',
  comparisonStatus,
  variant = 'default',
  showDeploymentTag = false,
  isDeployed = false,
}) => {
  const sitCount = rule.sitNames.length;
  const labelCount = rule.labelNames.length;
  
  // Determine text color based on comparison status (green for deployed only)
  const ruleNameColor = comparisonStatus === 'deployed' ? COLORS.success : 'inherit';
  const paddingLeft = RULE_ROW_PADDING[variant];
  
  return (
    <div style={{ opacity: isDisabled ? 0.5 : 1 }}>
      <div style={RULE_ROW_STYLES.container}>
        {showCheckbox && (
          <Checkbox
            checked={isChecked}
            disabled={isDisabled}
            onChange={(_, data) => onCheckChange?.(!!data.checked)}
            title={isDisabled ? disabledReason : undefined}
            aria-label={`Select rule ${rule.name}`}
            input={{ 'aria-label': `Select rule ${rule.name}` }}
          />
        )}
        <Button
          appearance="subtle"
          size="small"
          icon={isExpanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
          onClick={onToggle}
          style={RULE_ROW_STYLES.expandButton}
          aria-label={isExpanded ? `Collapse ${rule.name} details` : `Expand ${rule.name} details`}
          title={isExpanded ? `Collapse ${rule.name} details` : `Expand ${rule.name} details`}
          aria-expanded={isExpanded}
        />
        <div style={RULE_ROW_STYLES.ruleInfo}>
          <div style={RULE_ROW_STYLES.ruleHeader}>
            <Text style={{ ...RULE_ROW_STYLES.ruleName, color: ruleNameColor }}>{rule.name}</Text>
            <Text style={{ fontSize: 12, color: COLORS.gray600 }}>
              {sitCount} SIT{sitCount !== 1 ? 's' : ''}, {labelCount} Label{labelCount !== 1 ? 's' : ''}
            </Text>
            {showDeploymentTag && (
              isDeployed ? (
                <Text style={TAG_STYLES.deployed}>
                  Deployed
                </Text>
              ) : (
                <Text style={TAG_STYLES.notDeployed}>
                  Not Deployed
                </Text>
              )
            )}
          </div>
          <Text style={RULE_ROW_STYLES.ruleDescription}>
            {rule.description}
          </Text>
        </div>
      </div>
      
      {/* Expanded content - SITs and Labels */}
      {isExpanded && (
        <div
          style={{
            ...RULE_ROW_STYLES.expandedContent,
            paddingLeft,
          }}
        >
          <div style={GRID_STYLES.twoColumn}>
            {/* SITs */}
            <div>
              <Text style={SECTION_HEADER_STYLES.default}>
                Sensitive Information Types ({sitCount})
              </Text>
              <ul style={GRID_STYLES.list}>
                {rule.sitNames.length > 0 ? rule.sitNames.map((sit) => (
                  <li key={sit} style={GRID_STYLES.listItem}>{sit}</li>
                )) : <li style={GRID_STYLES.listItem}><em>None</em></li>}
              </ul>
            </div>
            {/* Labels */}
            <div>
              <Text style={SECTION_HEADER_STYLES.default}>
                Sensitivity Labels ({labelCount})
              </Text>
              <ul style={GRID_STYLES.list}>
                {rule.labelNames.length > 0 ? rule.labelNames.map((label) => (
                  <li key={label} style={GRID_STYLES.listItem}>{label}</li>
                )) : <li style={GRID_STYLES.listItem}><em>None</em></li>}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
